import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useRef, useEffect } from "react"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import { FullBodyMriApproval } from "./fullBodyMriApproval.component"
import { FullBodyMriIntro } from "./fullBodyMriIntro.component"
import { listProductTypes } from '../../services/productType.service'
import { completeFlow } from "../../services/flow.service.js"
import FlowType from "../../enums/flowType.enum"
import ProductHelper from "../../helpers/product.helper"
import PayTo from "../../enums/payTo.enum"
import UserHelper from "../../helpers/user.helper"
import FieldType from "../../enums/fieldType.enum"

export const Step = {
  INTRO: 'intro',
  LOCATION: 'location',
  FACILITY: 'facility',
  BASIC_INFO: 'basic-info',
  PREGNANCY: 'pregnancy',
  DISQUALIFIED: 'disqualified',
  CONTACT: 'contact',
  PAY: 'pay',
  ADDITIONAL_INFO: 'additional-info',
  PASSWORD: 'password',
  CONFIRM: 'confirm',
}

export const FullBodyMriFlow = () => {
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [productTypes, setProductTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState(null)
  const [initialUser, setInitialUser] = useState()
  const [selectedProductTypeCode, setSelectedProductTypeCode] = useState(null)

  useEffect(() => {
    fetchSkeleton()
  }, [productTypes, hasPass, initialUser, instalabMembership, flow, currentUser, selectedMenuItem, selectedProductTypeCode])

  useEffect(() => {
    const fetchProductTypes = async () => {
      const response = await listProductTypes({
        filter: {
          code: {
            $in: [
              ProductTypeCode.FULL_BODY_MRI,
              ProductTypeCode.PRENUVO,
              ProductTypeCode.EZRA_FULL_BODY_PLUS,
              ProductTypeCode.SIMONONE_BODYPLUS,
              ProductTypeCode.CONCIERGE_FEE,
            ]
          }
        }
      })
      setProductTypes(response)
    }
    fetchProductTypes()
  }, [])
  

  const getProductTypeCode = (productTypeId) => {
    return productTypes?.find(productType => productType._id === productTypeId)?.code
  }

  useEffect(() => {
    if (flow?.facility && flow.facility.menu) {
      const productTypeIds = [getProductType(ProductTypeCode.FULL_BODY_MRI)?._id, getProductType(ProductTypeCode.PRENUVO)?._id, getProductType(ProductTypeCode.SIMONONE_BODYPLUS)?._id]
      const product = flow.facility.menu.find(f => productTypeIds.includes(f.productType));

      setSelectedMenuItem(product);
      setSelectedProductTypeCode(getProductTypeCode(product?.productType));
    }
  }, [flow, productTypes]);


  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }

 
  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser || !productTypes) return

    let totalCost;
    if (selectedProductTypeCode) {
      totalCost = ProductHelper.getTotalCostV2({
        productTypes: [getProductType(selectedProductTypeCode), getProductType(ProductTypeCode.CONCIERGE_FEE)],
        instalabMembership,
        currentUser,
        priceOverride: selectedMenuItem?.payTo === PayTo.INSTALAB ? [{
          code: selectedProductTypeCode,
          price: selectedMenuItem?.price
        }] : null
      })  
    }


    const hasCredit = UserHelper.hasCredit(currentUser, selectedProductTypeCode)

    setSkeleton({
      [Step.INTRO]: {
        type: StepType.CUSTOM,
        nextStep: Step.LOCATION,
        content: ({ footer }) => <FullBodyMriIntro footer={footer} productType={getProductType(ProductTypeCode.FULL_BODY_MRI)} />
      },
      [Step.LOCATION]: {
        type: StepType.LOCATION,
        buildUser: true,
        buildProduct:true,
        nextStep: Step.FACILITY,
        title: `What's your current address?`,
        description: `We'll match you to nearby imaging centers that offer full body MRI.`,
        field: {
          name: 'location'
        },
      },
      [Step.FACILITY]: {
        type: StepType.FACILITY,
        nextStep: Step.BASIC_INFO,
        buildFlow: true,
        productTypeCode: [
          ProductTypeCode.PRENUVO, 
          ProductTypeCode.SIMONONE_BODYPLUS,
          ProductTypeCode.EZRA_FULL_BODY_PLUS,
        ], 
        addendumProductTypeCode: [ProductTypeCode.CONCIERGE_FEE],
        showPrice: !hasCredit,
        title: `Choose your preferred location.`,
        description: `We partner with top-rated imaging centers. Select the one that works best for you.`,
        field: {
          name: 'facility',
          required: true,
        },
      },

      [Step.BASIC_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.CONTACT,
        buildUser: true,
        title: `Let's get to know you.`,
        description: `We just need a few details to prepare your scan order and book your appointment at ${flow?.facility?.name}.`,
        fields: [
          {
            type: "group",
            label: "Full Name",
            required: true,
            fields: [
              {
                name: 'firstName',
                placeholder: 'First Name',
                label: 'First Name',
                required: true,
                type: 'text',
              },  
              {
                name: 'lastName',
                placeholder: 'Last Name',
                label: 'Last Name',
                required: true,
                type: 'text',
              },
            ]
          },
          {
            name: 'dob',
            label: 'Date of Birth',
            required: true,
            type: 'date',
            placeholder: 'MM/DD/YYYY',
          },
          {
            name: 'gender',
            label: 'Biological Sex',
            required: true,
            type: 'select',
            multiple:false,
            options: [
              {
                label: 'Male',
                value: Gender.MALE,
              }, {
                label: 'Female',
                value: Gender.FEMALE,
              }
            ],
          },
        ],
        skipIf: () => {
          return hasPass && (initialUser?.gender && initialUser?.dob && initialUser?.firstName && initialUser?.lastName)
        }
      },

 
      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PAY,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your appointment confirmation, directions and results here.",
        fields: [
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return hasPass && (initialUser?.email && initialUser?.phone)
        }
      },


      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.ADDITIONAL_INFO,
        addProduct: true,
        priceOverride: () => {
          return (selectedMenuItem?.payTo === PayTo.INSTALAB)? [{
            code: selectedProductTypeCode,
            price: selectedMenuItem?.price
          }] : null
        },
        productType: [selectedProductTypeCode, ProductTypeCode.CONCIERGE_FEE],
        title: () => {
          
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {
            return  <>Pay <span className="true-price">${totalCost}</span> for your <span className="product-name">{getProductType(selectedProductTypeCode).title}</span>.</>
          }

          else {
            return  <>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">{getProductType(ProductTypeCode.CONCIERGE_FEE).title.toLowerCase()}</span> to get started.</>
          }
  
        },
        description: () => {
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {  
            return <>Includes everything: appointment scheduling, the MRI scan, and radiologist-reviewed results.</>
          }
          else {
            return <>This fee covers appointment scheduling, the scan and the MRI report. {!hasCredit && <p>Note: The remaining {selectedMenuItem?.price && ` $${selectedMenuItem?.price}`} will be paid directly to {flow?.facility ? flow?.facility.name : 'the facility'} at time of your appointment.</p>} </>
          }
        },
        skipIf: () => {
          return (totalCost === 0) || hasCredit
        },
      },

      [Step.ADDITIONAL_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        title: `One last step to finalize your scan order.`,
        description: `We need a few final details to complete your order and ensure scan safety.`,
        fields: [

          {
            type: FieldType.GROUP,     
            required: true,
            label: <span className="highlight-label">What is your height and weight?</span>,
            fields: [
              {
                name: 'height',
                placeholder: 'Enter your height ...',
                required: true,
                type: 'text',
                unit: 'inches',
              },  
              {
                name: 'weight',
                placeholder: 'Enter your weight ...',
                required: true,
                type: 'text',
                unit: 'lbs',
              },
            ]
          },

          {
            type: FieldType.TEXTAREA,
            name: 'freeformAllergies',
            label: <span className="highlight-label">List any allergies you have.</span>,
            placeholder: 'Separate each allergy with a new line.',
          },         

          {
            type: FieldType.TEXTAREA,
            name: 'freeformMedications',
            label: <span className="highlight-label">List any medications you take.</span>,
            placeholder: 'Separate each medication with a new line.',
          },         

        ],
      },

      

      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Create a password for your Instalab account.`,
        description: `This lets you securely access your results, manage appointments, and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },

      [Step.CONFIRM]: {
        type: StepType.STATIC,
        path: `/flow/${FlowType.INTAKE}`,
        title: `All set! `,
        description: <>We'll email you in 1 business day to schedule your appointment. Please hit 'Continue' to tell more about yourself so we can personalize your results report.</>,
        buttonText: 'Continue',
        content: <FullBodyMriApproval flow={flow} />,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },
    })

  
  }
  

  return ( <>
    <Flow 
      skeleton={skeleton} 
      flow={flow} 
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      startIndex={0}
      productTypeCode={[ProductTypeCode.CLEERLY, ProductTypeCode.CONCIERGE_FEE]}
    />
    </>
  )
}